import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Klydo } from "../../../api/klydo";
import collectionApiClient, {
  PopulatedApprovedCollection,
} from "../../../api/collection";

export type CollectionStatus = "approved" | "draft";

const useApprovedCollectionsInfiniteQuery = (
  textSearch: string,
  limit: number = 10,
  returnCreator: boolean = true,
  sort?: Record<string, 1 | -1>,
  filter?: Record<string, unknown>,
  status: CollectionStatus = "approved",
) => {
  const queryClient = useQueryClient();

  const fetchCollections = async (
    pageParam: {
      skip?: number;
      limit?: number;
      filter?: Record<string, unknown>;
      returnCreator?: boolean;
      sort?: Record<string, 1 | -1>;
    },
    queryClient: ReturnType<typeof useQueryClient>,
    query: string,
  ): Promise<PopulatedApprovedCollection[]> => {
    const { skip = 0, limit, filter, sort } = pageParam;
    const params = {
      query,
      skip,
      limit,
      filter: filter ?? {},
      sort,
      returnCreator,
    };
    const res =
      status === "draft"
        ? ((await collectionApiClient.getDraftCollections(
            params,
          )) as unknown as PopulatedApprovedCollection[])
        : await collectionApiClient.getApprovedCollections(params);

    res.forEach((collection) => {
      queryClient.setQueryData(["collection", collection.id], collection);
    });

    const klydos = res
      .map((k) => k.klydos)
      .flat()
      .filter(Boolean) as Klydo[];

    for (const klydo of klydos) {
      queryClient.setQueryData(["klydo", klydo.id], klydo);
    }

    return res;
  };

  const { data, fetchNextPage, ...rest } = useInfiniteQuery<
    PopulatedApprovedCollection[],
    Error
  >({
    queryKey: ["collections", status, textSearch, sort, filter],
    queryFn: ({ pageParam }) =>
      fetchCollections(
        { ...(pageParam ?? { skip: 0, limit }), sort, filter },
        queryClient,
        textSearch,
      ),
    getNextPageParam: (lastPage, allPages) => {
      const currentLength = allPages.flat()?.length;
      return currentLength < limit ? undefined : { skip: currentLength, limit };
    },
    initialPageParam: { skip: 0, limit, sort, filter },
  });
  return {
    data: data?.pages.flat(),
    fetchNextPage,
    ...rest,
  };
};
export default useApprovedCollectionsInfiniteQuery;
